import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

export interface AppConfigInterface {
  remoteRefreshTriggerKey: string
  offlineMode: boolean
  firebaseHost: string
  firebasePort: string
  room: string
  precinctLabel: string
  fullscreen: boolean
  defaultAgent: boolean
}

const defaultAppConfig = {
  remoteRefreshTriggerKey: '',
  offlineMode: false,
  firebaseHost: '',
  firebasePort: '',
  room: '',
  precinctLabel: '',
  fullscreen: false,
  defaultAgent: false,
}

let initialState = defaultAppConfig as AppConfigInterface

const appConfig = localStorage.getItem('appConfig')

if (appConfig) {
  initialState = JSON.parse(appConfig)
}

export const saveRoomToStorage = (
  appConfig: AppConfigInterface,
  room: string
) => {
  const updatedAppConfig: AppConfigInterface = {
    ...appConfig,
    room: room.toLowerCase() === DEFAULT_SHOWCASE_ROOM ? '' : room,
  }
  localStorage.setItem('appConfig', JSON.stringify(updatedAppConfig))
  return updatedAppConfig
}

export const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setRemoteRefreshTriggerKey: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ remoteRefreshTriggerKey: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setOfflineMode: (
      state: AppConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      const newState = {
        ...state,
        ...{ offlineMode: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setFirebaseHost: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ firebaseHost: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setFirebasePort: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ firebasePort: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setRoom: (state: AppConfigInterface, action: PayloadAction<string>) => {
      const newState = saveRoomToStorage(state, action.payload)
      return {
        ...state,
        ...newState,
      }
    },
    setFullScreen: (
      state: AppConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      const newState = {
        ...state,
        ...{ fullscreen: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setPrecinctLabel: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ precinctLabel: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    setDefaultAgent: (state, action: PayloadAction<boolean>) => {
      const newState = {
        ...state,
        ...{ defaultAgent: action.payload },
      }
      localStorage.setItem('appConfig', JSON.stringify(newState))
      return newState
    },
    resetAppConfig: () => {
      localStorage.removeItem('appConfig')
      return defaultAppConfig
    },
  },
})

export const {
  setRemoteRefreshTriggerKey,
  setOfflineMode,
  setFirebaseHost,
  setFirebasePort,
  setRoom,
  setFullScreen,
  setPrecinctLabel,
  setDefaultAgent,
  resetAppConfig,
} = appConfigSlice.actions

export const selectAppConfig = (state: RootStateTypeExtra) => state.appConfig

export const selectCurrentRoom = (state: RootStateTypeExtra) => {
  const room = state.appConfig?.room || DEFAULT_SHOWCASE_ROOM
  const isDefault = room.toLowerCase() === DEFAULT_SHOWCASE_ROOM

  if (isDefault) {
    return DEFAULT_SHOWCASE_ROOM
  }

  return room
}

export default appConfigSlice.reducer
